import { useFormAugmentedContext } from '@framework/form';
import { ValidationError } from '@framework/validation';
import { groupBy, mapKeys, mapValues } from 'lodash';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IEngineSection } from '../../schema/sections/IEngineSection';
import {
  VALIDATION_TYPE,
  ValidationType,
} from '../form-builder/types/FormBuilderTypes';
import {
  IRuntimeSectionsStatusMonitorContext,
  RuntimeSectionsStatusMonitorContext,
} from './RuntimeSectionStatusMonitorContext';
import { SectionsStatusKey } from './SectionStatusSchema';

export interface IRuntimeSectionStatusMonitorProps {
  children: React.ReactNode;
  sections: IEngineSection[];
}

/**
 * Sistema di monitoring sulle sezioni in fase di compilazione.
 * Calcola lo stato di validazione delle sezioni e lo rende disponibile
 * tramite il Context `RuntimeSectionsStatusMonitorContext`.
 */
export function RuntimeSectionStatusMonitor(
  props: IRuntimeSectionStatusMonitorProps,
) {
  const { sections } = props;
  const { schema, validationContext } = useFormAugmentedContext();
  const methods = useFormContext();

  // Aggiorniamo gli errori sulle sezioni solo al cambio touched / opened
  const sectionsStatus = useWatch({ name: SectionsStatusKey });

  // TODO L'implementazione è pesante e bisognerebbe migliorare i trigger
  // dell'effect per assicurarsi che sia chiamato _solo e solo se_ necessario
  const { allErrors, sectionsErrors } = useMemo(() => {
    const allValues = methods.getValues();
    // console.log(`[RuntimeSectionStatusMonitor] allValues`, allValues);
    try {
      schema.validateSync(allValues, {
        abortEarly: false,
        context: {
          ...validationContext,
          [VALIDATION_TYPE]: ValidationType.COMPLETE,
        },
      });
      return { allErrors: [], sectionsErrors: {} };
    } catch (error) {
      if (error instanceof ValidationError) {
        const allErrors = flattenValidationErrors(error);

        // Raggruppiamo gli errori per sezione
        const stepIndexesToErrors = groupBy(
          allErrors.filter(e => getMetaInParams(e).step != null),
          e => getMetaInParams(e).step as number | null,
        );

        // Convertiamo gli indici numerici in ID delle sezioni
        return {
          allErrors,
          sectionsErrors: mapKeys(stepIndexesToErrors, (value, key) => {
            return sections[Number(key)].id;
          }),
        };
      }
      throw error;
    }
  }, [methods, schema, sections, validationContext]);

  /**
   * Consolidiamo le informazioni di Status delle sezioni con gli Errori
   * ritornati dalla validazione _completa_
   */
  const contextValue: IRuntimeSectionsStatusMonitorContext = useMemo(() => {
    // Non possiamo usare `sections` direttamente perché potrebbe contenere
    // sezioni non visibili, quindi per la validazione e per la visualizzazione
    // non devono esistere.
    return {
      errors: allErrors,
      sections: mapValues(sectionsStatus, (status, sectionId) => {
        return {
          ...status,
          errors: sectionsErrors[sectionId] ?? [],
        };
      }),
    };
  }, [sectionsErrors, sectionsStatus, allErrors]);

  return (
    <RuntimeSectionsStatusMonitorContext.Provider value={contextValue}>
      {props.children}
    </RuntimeSectionsStatusMonitorContext.Provider>
  );
}

function getMetaInParams(error: ValidationError): Record<string, any> {
  if (
    error?.params?.spec &&
    typeof error.params.spec === 'object' &&
    'meta' in error.params.spec
  ) {
    return error.params.spec.meta ?? {};
  }
  return {};
}

function flattenValidationErrors(error: ValidationError) {
  const flattened = [error];
  error.inner.forEach(e => {
    flattened.push(...flattenValidationErrors(e));
  });
  return flattened;
}
